"use client";

import Image from "next/image";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

interface GitHubUser {
  login: string;
  name: string | null;
  avatar_url: string;
  bio: string | null;
  public_repos: number;
  followers: number;
  following: number;
  location: string | null;
  company: string | null;
  blog: string | null;
  created_at: string;
}

interface GitHubProfileCardProps {
  user: GitHubUser;
  totalStars: number;
  totalForks: number;
}

export function GitHubProfileCard({ user, totalStars, totalForks }: GitHubProfileCardProps) {
  const { t } = useTranslation();
  const joinYear = new Date(user.created_at).getFullYear();

  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/5 via-background to-secondary/5 p-6 border border-border/50">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full blur-3xl -z-10" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-secondary/10 rounded-full blur-3xl -z-10" />

      <div className="flex flex-col md:flex-row gap-6 items-start md:items-center">
        {/* Avatar */}
        <div className="relative">
          <div className="w-24 h-24 md:w-32 md:h-32 rounded-full overflow-hidden ring-4 ring-primary/20">
            <Image
              src={user.avatar_url}
              alt={user.name || user.login}
              width={128}
              height={128}
              className="object-cover w-full h-full"
            />
          </div>
          <a
            href={`https://github.com/${user.login}`}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute -bottom-2 -right-2 bg-background border border-border rounded-full p-2 hover:bg-accent transition-colors"
          >
            <Icon icon="mdi:github" className="w-5 h-5" />
          </a>
        </div>

        {/* Info */}
        <div className="flex-1 space-y-3">
          <div>
            <h2 className="text-2xl font-bold">
              {user.name || user.login}
            </h2>
            <p className="text-muted-foreground">@{user.login}</p>
          </div>

          {user.bio && (
            <p className="text-sm text-muted-foreground max-w-md">
              {user.bio}
            </p>
          )}

          <div className="flex flex-wrap gap-4 text-sm">
            {user.location && (
              <div className="flex items-center gap-1 text-muted-foreground">
                <Icon icon="solar:map-point-linear" className="w-4 h-4" />
                <span>{user.location}</span>
              </div>
            )}
            {user.company && (
              <div className="flex items-center gap-1 text-muted-foreground">
                <Icon icon="solar:buildings-2-linear" className="w-4 h-4" />
                <span>{user.company}</span>
              </div>
            )}
            <div className="flex items-center gap-1 text-muted-foreground">
              <Icon icon="solar:calendar-linear" className="w-4 h-4" />
              <span>{t("Stats.profile.joined", { year: joinYear })}</span>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4 w-full md:w-auto">
          <div className="text-center p-3 rounded-xl bg-background/80 border border-border/50">
            <div className="text-2xl font-bold text-primary">{user.public_repos}</div>
            <div className="text-xs text-muted-foreground">{t("Stats.profile.repos")}</div>
          </div>
          <div className="text-center p-3 rounded-xl bg-background/80 border border-border/50">
            <div className="text-2xl font-bold text-primary">{user.followers}</div>
            <div className="text-xs text-muted-foreground">{t("Stats.profile.followers")}</div>
          </div>
          <div className="text-center p-3 rounded-xl bg-background/80 border border-border/50">
            <div className="text-2xl font-bold text-secondary">{totalStars}</div>
            <div className="text-xs text-muted-foreground">{t("Stats.profile.stars")}</div>
          </div>
          <div className="text-center p-3 rounded-xl bg-background/80 border border-border/50">
            <div className="text-2xl font-bold text-secondary">{user.following}</div>
            <div className="text-xs text-muted-foreground">{t("Stats.profile.following")}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
